import { useState, useCallback, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import MenuIcon from '@material-ui/icons/Menu';
import { SidebarGroup, useSidebarOpenState } from '@backstage/core-components';
import { useApi, storageApiRef, alertApiRef } from '@backstage/core-plugin-api';
import { CustomSidebarItem } from './CustomSidebarItem';
import { MOBILE_MEDIA_QUERY } from './mobileBreakpoint';
import {
  SIDEBAR_ITEMS_CONFIG,
  STORAGE_BUCKET,
  MENU_ORDER_KEY,
  DEFAULT_MENU_ORDER,
} from './sidebarConstants';
import { useSidebarPreferences } from './SidebarPreferencesContext';
import { useFeatureFlag } from '@internal/plugin-feature-flags-common/components';
import {
  ADR_FEATURE_FLAG,
  GOLDEN_PATHS_FEATURE_FLAG,
  MY_AI_TOOLS_FEATURE_FLAG,
} from '@internal/plugin-feature-flags-common';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import {
  UnprocessedEntitiesSidebarItem,
  DatadogAnalyticsSidebarItem,
} from './AdminSidebarItems';
import { HiddenItemsSidebarSection } from './HiddenItemsSidebarSection';

const useStyles = makeStyles(theme => ({
  draggableItem: {
    position: 'relative',
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  dragging: {
    opacity: 0.5,
    background: theme.palette.action.hover,
  },
  dragOver: {
    borderTop: `2px solid ${theme.palette.primary.main}`,
  },
  dragHandle: {
    position: 'absolute',
    left: 4,
    top: '50%',
    transform: 'translateY(-50%)',
    color: theme.palette.text.secondary,
    opacity: 0,
    transition: 'opacity 0.2s ease',
    cursor: 'grab',
    zIndex: 1,
    '&:active': {
      cursor: 'grabbing',
    },
  },
  draggableWrapper: {
    position: 'relative',
    '&:hover $dragHandle': {
      opacity: 0.6,
    },
  },
  focused: {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '-2px',
  },
}));

export const DraggableMenuSection = () => {
  const classes = useStyles();
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY, { noSsr: true });
  const alertApi = useApi(alertApiRef);
  const baseStorageApi = useApi(storageApiRef);
  const { hiddenItems } = useSidebarPreferences();
  const [menuOrder, setMenuOrder] = useState<string[]>(DEFAULT_MENU_ORDER);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const { isOpen } = useSidebarOpenState();
  const { enabled: isAdrEnabled } = useFeatureFlag(ADR_FEATURE_FLAG);
  const { enabled: isGoldenPathsEnabled } = useFeatureFlag(
    GOLDEN_PATHS_FEATURE_FLAG,
  );
  const { enabled: isMyAiToolsEnabled } = useFeatureFlag(
    MY_AI_TOOLS_FEATURE_FLAG,
  );

  // Load menu order from storage
  useEffect(() => {
    const storageApi = baseStorageApi.forBucket(STORAGE_BUCKET);
    const subscription = storageApi
      .observe$<string[]>(MENU_ORDER_KEY)
      .subscribe({
        next: snapshot => {
          if (snapshot.value && Array.isArray(snapshot.value)) {
            // Merge with default order to include any new items
            const storedOrder = snapshot.value;
            const newItems = DEFAULT_MENU_ORDER.filter(
              item => !storedOrder.includes(item),
            );
            setMenuOrder([...storedOrder, ...newItems]);
          }
        },
      });
    return () => subscription.unsubscribe();
  }, [baseStorageApi]);

  // Drag and drop handlers
  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, route: string) => {
      setDraggedItem(route);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', route);
    },
    [],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
    setDragOverItem(null);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>, route: string) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (draggedItem && draggedItem !== route) {
        setDragOverItem(route);
      }
    },
    [draggedItem],
  );

  const handleDragLeave = useCallback(() => {
    setDragOverItem(null);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>, targetRoute: string) => {
      e.preventDefault();
      setDragOverItem(null);

      if (!draggedItem || draggedItem === targetRoute) {
        return;
      }

      // Reorder menu items
      const newOrder = [...menuOrder];
      const draggedIndex = newOrder.indexOf(draggedItem);
      const targetIndex = newOrder.indexOf(targetRoute);

      if (draggedIndex === -1 || targetIndex === -1) {
        return;
      }

      // Remove dragged item and insert at new position
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedItem);

      // Update state immediately for responsive UI
      setMenuOrder(newOrder);

      // Save new order to storage
      try {
        const storageApi = baseStorageApi.forBucket(STORAGE_BUCKET);
        await storageApi.set(MENU_ORDER_KEY, newOrder);
        setDraggedItem(null);
      } catch (error) {
        // Error is shown to user via alertApi
        alertApi.post({
          message: 'Failed to reorder menu. Please try again.',
          severity: 'error',
          display: 'transient',
        });
        // Revert to previous order
        setMenuOrder(menuOrder);
        setDraggedItem(null);
      }
    },
    [draggedItem, menuOrder, baseStorageApi, alertApi],
  );

  // Filter out hidden items, missing config, and feature-flagged routes
  const visibleItems = menuOrder.filter(route => {
    if (hiddenItems.includes(route) || !SIDEBAR_ITEMS_CONFIG[route]) {
      return false;
    }
    if (route === '/golden-paths' && !isGoldenPathsEnabled) {
      return false;
    }
    if (route === '/adr' && !isAdrEnabled) {
      return false;
    }
    if (route === '/my-ai-tools' && !isMyAiToolsEnabled) {
      return false;
    }
    return true;
  });

  // Keyboard accessibility: Move items up/down with Alt+Arrow keys
  const handleKeyDown = useCallback(
    async (
      e: React.KeyboardEvent<HTMLDivElement>,
      route: string,
      index: number,
    ) => {
      if (!e.altKey) return;

      let newIndex: number | null = null;
      if (e.key === 'ArrowUp' && index > 0) {
        newIndex = index - 1;
      } else if (e.key === 'ArrowDown' && index < visibleItems.length - 1) {
        newIndex = index + 1;
      }

      if (newIndex === null) return;

      e.preventDefault();
      const newOrder = [...menuOrder];
      const currentIndex = newOrder.indexOf(route);
      const targetRoute = visibleItems[newIndex];
      const targetIndex = newOrder.indexOf(targetRoute);

      if (currentIndex === -1 || targetIndex === -1) return;

      // Swap items
      newOrder.splice(currentIndex, 1);
      newOrder.splice(targetIndex, 0, route);

      setMenuOrder(newOrder);
      setFocusedIndex(newIndex);

      try {
        const storageApi = baseStorageApi.forBucket(STORAGE_BUCKET);
        await storageApi.set(MENU_ORDER_KEY, newOrder);
      } catch (error) {
        alertApi.post({
          message: 'Failed to reorder menu. Please try again.',
          severity: 'error',
          display: 'transient',
        });
        setMenuOrder(menuOrder);
      }
    },
    [menuOrder, visibleItems, baseStorageApi, alertApi],
  );

  const menuItems = (
    <>
      <div role="listbox" aria-label="Reorderable menu items">
          {visibleItems.map((route, index) => {
            const config = SIDEBAR_ITEMS_CONFIG[route];
            if (!config) return null;

            // Special handling for devtools (permission-gated)
            if (route === '/devtools') {
              return (
                <RequirePermission
                  key={route}
                  permission={devToolsAdministerPermission}
                  errorPage={<></>}
                >
                  <div
                    className={`${classes.draggableWrapper} ${
                      draggedItem === route ? classes.dragging : ''
                    } ${dragOverItem === route ? classes.dragOver : ''} ${
                      focusedIndex === index ? classes.focused : ''
                    }`}
                    draggable
                    tabIndex={0}
                    onDragStart={e => handleDragStart(e, route)}
                    onDragEnd={handleDragEnd}
                    onDragOver={e => handleDragOver(e, route)}
                    onDragLeave={handleDragLeave}
                    onDrop={e => handleDrop(e, route)}
                    onFocus={() => setFocusedIndex(index)}
                    onBlur={() => setFocusedIndex(null)}
                    onKeyDown={e => handleKeyDown(e, route, index)}
                    role="option"
                    aria-selected={focusedIndex === index}
                    aria-label={`${config.text} - menu item ${index + 1} of ${
                      visibleItems.length
                    }. Use Alt+Arrow keys to reorder.`}
                  >
                    {isOpen && (
                      <DragIndicatorIcon
                        className={classes.dragHandle}
                        style={{ fontSize: 16 }}
                        aria-hidden="true"
                      />
                    )}
                    <CustomSidebarItem
                      icon={config.icon}
                      to={route}
                      text={config.text}
                    />
                  </div>
                </RequirePermission>
              );
            }

            return (
              <div
                key={route}
                className={`${classes.draggableWrapper} ${
                  draggedItem === route ? classes.dragging : ''
                } ${dragOverItem === route ? classes.dragOver : ''} ${
                  focusedIndex === index ? classes.focused : ''
                }`}
                draggable
                tabIndex={0}
                onDragStart={e => handleDragStart(e, route)}
                onDragEnd={handleDragEnd}
                onDragOver={e => handleDragOver(e, route)}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDrop(e, route)}
                onFocus={() => setFocusedIndex(index)}
                onBlur={() => setFocusedIndex(null)}
                onKeyDown={e => handleKeyDown(e, route, index)}
                role="option"
                aria-selected={focusedIndex === index}
                aria-label={`${config.text} - menu item ${index + 1} of ${
                  visibleItems.length
                }. Use Alt+Arrow keys to reorder.`}
              >
                {isOpen && (
                  <DragIndicatorIcon
                    className={classes.dragHandle}
                    style={{ fontSize: 16 }}
                    aria-hidden="true"
                  />
                )}
                <CustomSidebarItem
                  icon={config.icon}
                  to={route}
                  text={config.text}
                />
              </div>
            );
          })}
      </div>
      <UnprocessedEntitiesSidebarItem />
      <DatadogAnalyticsSidebarItem />
      <HiddenItemsSidebarSection />
    </>
  );

  if (isMobile) {
    return menuItems;
  }

  return <SidebarGroup label="Menu" icon={<MenuIcon />}>{menuItems}</SidebarGroup>;
};

export default DraggableMenuSection;
